import React, { useEffect } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

import styles from './LegalModal.module.css';

export type LegalDocumentType = 'privacy' | 'terms';

const LEGAL_DOCUMENTS: Record<LegalDocumentType, { title: string; src: string }> = {
  privacy: {
    title: 'Privacy Policy',
    src: '/Policyora%20-%20Privacy%20and%20Policy.pdf',
  },
  terms: {
    title: 'Terms & Conditions',
    src: '/Policyora%20-%20Terms%20%26%20Conditions.pdf',
  },
};

interface LegalModalProps {
  open: boolean;
  documentType: LegalDocumentType;
  onSelectDocument: (documentType: LegalDocumentType) => void;
  onClose: () => void;
  showAcceptButton?: boolean;
  onAccept?: () => void;
}

export const LegalModal: React.FC<LegalModalProps> = ({
  open,
  documentType,
  onSelectDocument,
  onClose,
  showAcceptButton = false,
  onAccept,
}) => {
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const activeDocument = LEGAL_DOCUMENTS[documentType];

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close legal document">
            ×
          </button>
        </div>

        <div className={styles.tabRow}>
          <button
            type="button"
            className={`${styles.tab} ${documentType === 'privacy' ? styles.tabActive : ''}`}
            onClick={() => onSelectDocument('privacy')}
          >
            Privacy Policy
          </button>
          <button
            type="button"
            className={`${styles.tab} ${documentType === 'terms' ? styles.tabActive : ''}`}
            onClick={() => onSelectDocument('terms')}
          >
            Terms & Conditions
          </button>
        </div>

        <div className={styles.viewerWrap}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div className={styles.viewer}>
              <Viewer
                fileUrl={activeDocument.src}
                defaultScale={SpecialZoomLevel.PageWidth}
              />
            </div>
          </Worker>
        </div>

        <div className={styles.footer}>
          <div className={styles.actions}>
            <button type="button" className={styles.secondaryButton} onClick={onClose}>
              Close
            </button>
            {showAcceptButton && onAccept && (
              <button type="button" className={styles.acceptButton} onClick={onAccept}>
                Accept and continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
